import { useLocalStorage, useSessionStorage } from '@vueuse/core'

const detectIsEmbedded = () => {
  if (typeof window === 'undefined') {
    return false
  }

  const params = new URLSearchParams(window.location.search)

  if (params.get('embed') === 'true') {
    return true
  }

  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

export const useUIStore = defineStore('ui', () => {
  const isCapsLockOn = ref(false)
  const menuMode = useLocalStorage('menuMode', 'normal')
  const isEmbedded = ref(detectIsEmbedded())
  const isPwaMode = useSessionStorage('isPwaMode', false)
  const presenceStatuses = ['online', 'away', 'dnd', 'offline']
  const presenceStatus = ref('online')
  const presenceStatusLocked = ref(false)
  const presenceStatusLockReason = ref('')
  const presenceStatusBeforeMeeting = useSessionStorage('sharingCenterPresenceStatusBeforeMeeting', '')

  const meetingCode = useSessionStorage('meetingCode', '')
  const meetingToken = useSessionStorage('meetingToken', '')

  const lastSharingCenterRoute = ref({ name: 'sc-overview' })
  const lastStatisticsRoute = ref({ name: 'statistics-overview' })
  const lastLicensesRoute = ref({ name: 'licenses-omni' })
  const isSharingCenterMeetingActive = ref(false)
  const isSharingCenterMeetingLoading = ref(false)
  const isSharingCenterMeetingEnded = ref(false)
  const isSharingCenterMeetingError = ref(false)

  const sharingCenterMeeting = ref(null)
  const dynamicBreadcrumbLabel = ref('')

  const setMeetingCode = (code) => {
    meetingCode.value = String(code || '')
  }

  const setMeetingToken = (token) => {
    meetingToken.value = String(token || '')
  }

  const clearMeetingAuth = () => {
    meetingCode.value = ''
    meetingToken.value = ''
  }

  const clearSharingCenterMeeting = () => {
    sharingCenterMeeting.value = null
    isSharingCenterMeetingActive.value = false
    isSharingCenterMeetingLoading.value = false
    isSharingCenterMeetingEnded.value = false
    isSharingCenterMeetingError.value = false
    clearMeetingAuth()
  }

  const setSharingCenterMeetingActive = (state) => {
    isSharingCenterMeetingActive.value = state === true
  }

  const setSharingCenterMeetingLoading = (state) => {
    isSharingCenterMeetingLoading.value = state === true
  }

  const setSharingCenterMeetingEnded = (state) => {
    isSharingCenterMeetingEnded.value = state === true
  }

  const setSharingCenterMeetingError = (state) => {
    isSharingCenterMeetingError.value = state === true
  }

  const setSharingCenterMeeting = (meeting) => {
    sharingCenterMeeting.value = meeting || null
  }

  const setPresenceStatus = (status) => {
    const normalizedStatus = String(status || '').trim()
    presenceStatus.value = presenceStatuses.includes(normalizedStatus) ? normalizedStatus : 'online'
  }

  const setPresenceStatusLocked = (state, reason = '') => {
    presenceStatusLocked.value = state === true
    presenceStatusLockReason.value = presenceStatusLocked.value ? String(reason || '').trim() : ''
  }

  const setPresenceStatusBeforeMeeting = (status) => {
    const normalizedStatus = String(status || '').trim()
    presenceStatusBeforeMeeting.value = presenceStatuses.includes(normalizedStatus) ? normalizedStatus : ''
  }

  const setLastSharingCenterRoute = (to) => {
    const name = typeof to?.name === 'string' ? to.name : ''
    if (!name) {
      return
    }

    lastSharingCenterRoute.value = {
      name,
      params: to?.params && typeof to.params === 'object' ? { ...to.params } : undefined,
      query: to?.query && typeof to.query === 'object' ? { ...to.query } : undefined,
    }
  }

  const resetLastSharingCenterRoute = () => {
    lastSharingCenterRoute.value = { name: 'sc-overview' }
  }

  const setLastStatisticsRoute = (to) => {
    const name = typeof to?.name === 'string' ? to.name : ''
    if (!name) {
      return
    }

    lastStatisticsRoute.value = {
      name,
      params: to?.params && typeof to.params === 'object' ? { ...to.params } : undefined,
      query: to?.query && typeof to.query === 'object' ? { ...to.query } : undefined,
    }
  }

  const resetLastStatisticsRoute = () => {
    lastStatisticsRoute.value = { name: 'statistics-overview' }
  }

  const setLastLicensesRoute = (to) => {
    const name = typeof to?.name === 'string' ? to.name : ''
    if (!name) {
      return
    }

    lastLicensesRoute.value = {
      name,
      params: to?.params && typeof to.params === 'object' ? { ...to.params } : undefined,
      query: to?.query && typeof to.query === 'object' ? { ...to.query } : undefined,
    }
  }

  const resetLastLicensesRoute = () => {
    lastLicensesRoute.value = { name: 'licenses-omni' }
  }

  const setCapsLockState = (state) => {
    isCapsLockOn.value = state === true
  }

  const setPwaMode = (state) => {
    isPwaMode.value = state === true
  }

  const setDynamicBreadcrumbLabel = (label) => {
    dynamicBreadcrumbLabel.value = typeof label === 'string' ? label : ''
  }

  const clearDynamicBreadcrumbLabel = () => {
    dynamicBreadcrumbLabel.value = ''
  }

  const checkCapsLockState = (event) => {
    if (typeof event?.getModifierState !== 'undefined') {
      setCapsLockState(event.getModifierState('CapsLock'))
    }
  }

  let removeCapsLockListeners = null
  const addCapsLockListeners = () => {
    if (typeof window === 'undefined' || removeCapsLockListeners) {
      return
    }

    window.addEventListener('keydown', checkCapsLockState)
    window.addEventListener('keyup', checkCapsLockState)

    removeCapsLockListeners = () => {
      window.removeEventListener('keydown', checkCapsLockState)
      window.removeEventListener('keyup', checkCapsLockState)
      removeCapsLockListeners = null
    }
  }

  addCapsLockListeners()

  return {
    menuMode,
    isCapsLockOn,
    isEmbedded,
    isPwaMode,
    setPwaMode,

    presenceStatus,
    presenceStatusLocked,
    presenceStatusLockReason,
    presenceStatusBeforeMeeting,
    presenceStatuses,
    setPresenceStatus,
    setPresenceStatusLocked,
    setPresenceStatusBeforeMeeting,

    meetingCode,
    meetingToken,
    setMeetingCode,
    setMeetingToken,
    clearMeetingAuth,
    clearSharingCenterMeeting,

    isSharingCenterMeetingActive,
    setSharingCenterMeetingActive,

    isSharingCenterMeetingLoading,
    setSharingCenterMeetingLoading,

    isSharingCenterMeetingEnded,
    setSharingCenterMeetingEnded,

    isSharingCenterMeetingError,
    setSharingCenterMeetingError,

    sharingCenterMeeting,
    setSharingCenterMeeting,

    dynamicBreadcrumbLabel,
    setDynamicBreadcrumbLabel,
    clearDynamicBreadcrumbLabel,

    lastSharingCenterRoute,
    setLastSharingCenterRoute,
    resetLastSharingCenterRoute,

    lastStatisticsRoute,
    setLastStatisticsRoute,
    resetLastStatisticsRoute,

    lastLicensesRoute,
    setLastLicensesRoute,
    resetLastLicensesRoute,
  }
})
